// src/services/queryCache.js
//
// Remembering what the server already told us.
//
// ── The problem this solves ─────────────────────────────────────────────────
//
// Nothing in this app cached anything. Every screen refetched from scratch on
// every mount, and apiSession sets `cache: 'no-store'` so the browser could not
// help either. Navigating away and back meant skeletons and a wait for data
// that had not changed — on every single user action.
//
// ── Stale-while-revalidate ──────────────────────────────────────────────────
//
// The rule that makes an app feel instant: if we have an answer, show it NOW,
// and quietly check for a newer one. The screen is never blank when we have
// something to put there, and it is never wrong for longer than one round trip.
//
// That is a deliberate trade — a person may look at a few-seconds-old job list
// for a moment. For a job board that is nothing; for a bank balance it would be
// unacceptable. Anything here that must be exactly current should pass
// `force: true` rather than being cached carefully.
//
// ── Why not a library ───────────────────────────────────────────────────────
//
// TanStack Query does all of this and more, properly. It is the right answer at
// scale and a reasonable thing to move to later — the hook below is
// deliberately shaped like `useQuery` so that swap stays small. This exists
// because it is ~100 lines against a dependency plus a migration, and the
// problem is worth fixing today.

/** key -> { value, at, promise } */
const store = new Map();

/** How long an entry is served without a background refresh. */
const DEFAULT_TTL = 60_000;

// A cache that only grows is a leak. Job lists are large, and a person moving
// through pages can accumulate a lot of them in a session.
const MAX_ENTRIES = 120;

/** Everything currently cached, for tests and for the invalidation helpers. */
export function _entries() { return store; }

/**
 * The cached value for a key, right now, without a request.
 *
 * Synchronous on purpose. Every async read — even one that resolves from this
 * same store on the next microtask — costs a painted frame with nothing in it,
 * and a frame of skeleton on every navigation is the exact flicker this whole
 * module exists to remove. A screen seeds its first render from this and never
 * shows a loading state for something it already has.
 *
 * Returns undefined both when the key is missing and when the entry is only an
 * in-flight request. Both mean the same thing to a caller: nothing to paint.
 *
 * Staleness is not checked here. The caller paints what it has and lets the
 * read behind it refresh — that is the whole trade this cache makes.
 */
export function peek(key) {
  const hit = store.get(key);
  return hit && 'value' in hit ? hit.value : undefined;
}

/**
 * Reads through the cache.
 *
 * @param {string} key           anything that identifies the request
 * @param {() => Promise<any>} fetcher
 * @param {object} [opts]
 * @param {number} [opts.ttl]    ms before a value is considered stale
 * @param {boolean} [opts.force] ignore any cached value
 * @param {(value:any)=>void} [opts.onFresh] called when a revalidation lands
 * @returns {Promise<any>} the cached value immediately when there is one
 */
export async function cached(key, fetcher, { ttl = DEFAULT_TTL, force = false, onFresh } = {}) {
  const hit = store.get(key);
  const hasValue = hit && 'value' in hit;

  // ── An entry that is only an in-flight request ──
  //
  // Checked FIRST, and the order is the bug this had. A second caller arriving
  // while the first is still fetching sees an entry with a promise and no
  // value; falling through to the stale branch below returned `hit.value`,
  // which is undefined. Three components mounting together got the value once
  // and `undefined` twice.
  //
  // This is also what stops three identical requests going out for one list.
  if (!hasValue && hit?.promise && !force) return hit.promise;

  if (hasValue && !force) {
    if (Date.now() - hit.at < ttl) return hit.value;

    // Stale: hand back what we have and refresh behind it. The caller is told
    // through onFresh rather than by waiting, which is the whole point.
    revalidate(key, fetcher, onFresh);
    return hit.value;
  }

  const promise = fetcher()
    .then((value) => {
      // Only if this is still the entry the key holds. A write that called
      // invalidate() while this read was in flight — an import applied
      // while the switcher's mount read was still on its way back — must
      // not have its "no resume yet" list written back over it with a
      // fresh timestamp, to be served for the full TTL (2026-09-04). The
      // caller still gets the value; it just is not cached. Same guard as
      // the catch below already had.
      if (store.get(key)?.promise === promise) put(key, value);
      return value;
    })
    .catch((err) => {
      // A failed fetch must not poison the key. The next attempt should try
      // again rather than replay the rejection.
      const entry = store.get(key);
      if (entry?.promise === promise) store.delete(key);
      throw err;
    });

  store.set(key, { ...(hit || {}), promise });
  return promise;
}

function revalidate(key, fetcher, onFresh) {
  const entry = store.get(key);
  // Already refreshing — a person clicking around must not queue up refreshes.
  if (entry?.promise) return;

  const promise = fetcher()
    .then((value) => {
      // Same guard as cached(): invalidated meanwhile means do not cache.
      if (store.get(key)?.promise === promise) put(key, value);
      onFresh?.(value);
      return value;
    })
    .catch(() => {
      // A background refresh that fails is invisible on purpose: the cached
      // value is still on screen and still usable, and an error toast for
      // something the person did not ask for is noise.
      const e = store.get(key);
      if (e) delete e.promise;
    });

  store.set(key, { ...entry, promise });
}

function put(key, value) {
  store.set(key, { value, at: Date.now() });
  // Oldest first. Map preserves insertion order, so the first key is the
  // least recently written.
  if (store.size > MAX_ENTRIES) {
    const oldest = store.keys().next().value;
    if (oldest !== undefined) store.delete(oldest);
  }
}

/**
 * Drops cached entries whose key contains `fragment`.
 *
 * Called after a mutation. Saving a job changes the saved list and the badge on
 * every card, and showing a stale answer straight after the person's own action
 * is the one case where staleness reads as a bug rather than as speed.
 */
export function invalidate(fragment) {
  if (!fragment) { store.clear(); return; }
  for (const key of [...store.keys()]) {
    if (key.includes(fragment)) store.delete(key);
  }
}

/** Everything, on sign-out. One person's cache must never be shown to another. */
export function clearCache() { store.clear(); }
